import styles from './FamilyHeaderMedia.module.css';

import gql from 'graphql-tag';
import { type FC, useMemo, useState } from 'react';

import { FamilyHeaderMediaFragment } from 'client/graphql/types/operations';

export const FAMILY_HEADER_MEDIA_FRAGMENT = gql`
  fragment FamilyHeaderMediaFragment on FontFamily {
    headerMedia {
      url
      url800w: url(width: 800, quality: 90, format: "webp")
      url1400w: url(width: 1400, quality: 90, format: "webp")
      url2000w: url(width: 2000, quality: 90, format: "webp")
      url2600w: url(width: 2600, quality: 90, format: "webp")
      width
      height
    }

    headerMediaAspectRatio
    headerMediaHasAudio
  }
`;

interface Props {
  media: FamilyHeaderMediaFragment['headerMedia'];
  aspectRatio: FamilyHeaderMediaFragment['headerMediaAspectRatio'];
  hasAudio: FamilyHeaderMediaFragment['headerMediaHasAudio'];
}

const MIME_TYPES: { [extension: string]: string } = {
  mp4: 'video/mp4',
  webm: 'video/webm',
};

const FamilyHeaderMedia: FC<Props> = (props) => {
  const { media, aspectRatio, hasAudio } = props;

  const [muted, setMuted] = useState(true);

  const isVideo = useMemo(
    () => media?.url && /\.(mp4|webm)$/i.test(media.url),
    [media],
  );

  const videoType = useMemo(() => {
    const extension = media?.url?.split('.').pop();

    if (extension == null) {
      return;
    }

    return MIME_TYPES[extension];
  }, [media?.url]);

  if (media == null) {
    return null;
  }

  return (
    <div className={styles.container}>
      {media?.url &&
        (isVideo ? (
          <>
            <video
              className={styles.media}
              style={aspectRatio != null ? { aspectRatio } : undefined}
              autoPlay
              playsInline
              loop
              muted={muted}
              aria-hidden="true"
            >
              <source src={media.url} type={videoType} />
            </video>

            {hasAudio && (
              <MuteToggle
                muted={muted}
                onToggle={() => {
                  setMuted((state) => !state);
                }}
              />
            )}
          </>
        ) : (
          <img
            className={styles.media}
            src={media.url}
            srcSet={[
              [media.url800w, '800w'],
              [media.url1400w, '1400w'],
              [media.url2000w, '2000w'],
              [media.url2600w, '2600w'],
            ]
              .filter(([url]) => url)
              .map(([url, size]) => `${url} ${size}`)
              .join(', ')}
            sizes="100vw"
            width={media.width ?? undefined}
            height={media.height ?? undefined}
            aria-hidden="true"
          />
        ))}
    </div>
  );
};

const MuteToggle: FC<{ muted: boolean; onToggle: () => void }> = ({
  muted,
  onToggle,
}) => {
  return (
    <button className={styles.muteToggle} onClick={onToggle}>
      {muted ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28px"
          height="28px"
          viewBox="0 -960 960 960"
          fill="#1f1f1f"
        >
          <path d="M792-56 671-177q-25 16-53 27.5T560-131v-82q14-5 27.5-10t25.5-12L480-368v208L280-360H120v-240h128L56-792l56-56 736 736-56 56Zm-8-232-58-58q17-31 25.5-65t8.5-70q0-94-55-168T560-749v-82q124 28 202 125.5T840-481q0 53-14.5 102T784-288ZM650-422l-90-90v-130q47 22 73.5 66t26.5 96q0 15-2.5 29.5T650-422ZM480-592 376-696l104-104v208Zm-80 238v-94l-72-72H200v80h114l86 86Zm-36-130Z" />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28px"
          height="28px"
          viewBox="0 -960 960 960"
          fill="#1f1f1f"
        >
          <path d="M560-131v-82q90-26 145-100t55-168q0-94-55-168T560-749v-82q124 28 202 125.5T840-481q0 127-78 224.5T560-131ZM120-360v-240h160l200-200v640L280-360H120Zm440 40v-322q47 22 73.5 66t26.5 96q0 51-26.5 94.5T560-320ZM400-606l-86 86H200v80h114l86 86v-252ZM300-480Z" />
        </svg>
      )}
    </button>
  );
};

export default FamilyHeaderMedia;
